/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Widget Lead Capture Configuration Tab Component (Task 10.3.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useWidgetStudioStore } from '../../stores/useWidgetStudioStore';
import { useI18n } from '../../hooks/useI18n';
import { UserCheck, ShieldCheck } from 'lucide-react';

export const WidgetLeadCaptureTab: React.FC = () => {
  const { t } = useI18n();
  const { config, updateLeadCapture } = useWidgetStudioStore();
  const { leadCapture } = config;

  return (
    <div className="space-y-6">
      {/* 1. Master Toggle */}
      <div className="flex items-start justify-between p-4 rounded-2xl border border-surface-border bg-surface-elevated/40 gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-primary" />
            <span className="text-sm font-heading font-bold text-foreground">
              {t('widget.leadCaptureHeading')}
            </span>
          </div>
          <p className="text-xs text-foreground-muted">
            {t('widget.leadCaptureDesc')}
          </p>
        </div>

        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
          <input
            type="checkbox"
            checked={leadCapture.enabled}
            onChange={(e) => updateLeadCapture({ enabled: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-surface-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-surface-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
        </label>
      </div>

      {leadCapture.enabled && (
        <div className="space-y-5 animate-in fade-in duration-200">
          {/* Form Content Labels */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">{t('widget.leadFormTitle')}</label>
              <input
                type="text"
                value={leadCapture.title}
                onChange={(e) => updateLeadCapture({ title: e.target.value })}
                placeholder="e.g. Start chatting with Sophia"
                className="w-full px-3 py-2 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                {t('widget.leadSubmitBtnText')}
              </label>
              <input
                type="text"
                value={leadCapture.submitButtonText}
                onChange={(e) => updateLeadCapture({ submitButtonText: e.target.value })}
                placeholder="e.g. Start Conversation"
                className="w-full px-3 py-2 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">{t('widget.leadSubtitle')}</label>
            <input
              type="text"
              value={leadCapture.subtitle}
              onChange={(e) => updateLeadCapture({ subtitle: e.target.value })}
              placeholder="e.g. Please share your details so we can best assist you..."
              className="w-full px-3 py-2 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <hr className="border-surface-border" />

          {/* Fields Selection */}
          <div className="space-y-3">
            <h4 className="text-xs font-heading font-bold text-foreground uppercase tracking-wider">
              {t('widget.requiredFieldsHeading')}
            </h4>

            <div className="space-y-2.5">
              <label className="flex items-center gap-3 p-3 rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={leadCapture.requireName}
                  onChange={(e) => updateLeadCapture({ requireName: e.target.checked })}
                  className="w-4 h-4 rounded text-primary border-surface-border focus:ring-primary"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-foreground">{t('widget.fieldNameLabel')}</span>
                  <p className="text-[11px] text-foreground-muted">
                    {t('widget.fieldNameDesc')}
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={leadCapture.requireEmail}
                  onChange={(e) => updateLeadCapture({ requireEmail: e.target.checked })}
                  className="w-4 h-4 rounded text-primary border-surface-border focus:ring-primary"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-foreground">{t('widget.fieldEmailLabel')}</span>
                  <p className="text-[11px] text-foreground-muted">
                    {t('widget.fieldEmailDesc')}
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={leadCapture.requirePhone}
                  onChange={(e) => updateLeadCapture({ requirePhone: e.target.checked })}
                  className="w-4 h-4 rounded text-primary border-surface-border focus:ring-primary"
                />
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-foreground">{t('widget.fieldPhoneLabel')}</span>
                  <p className="text-[11px] text-foreground-muted">
                    {t('widget.fieldPhoneDesc')}
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div className="p-3.5 rounded-xl border border-emerald-500/20 bg-emerald-500/5 text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-2.5">
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            <span>
              {t('widget.leadSecurityGuarantee')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
